import { Component } from 'preact'
import { extractRenderPropsChildren } from '../../../libs/renderProps'
import DatePicker, {
  DateRange
} from '../../../preact-components/design-system-components/date-picker'
import InputGroup from '../../ui-components/InputGroup'
import { BaseFilterSelectorBuilderData, FilterSelectorProps } from './interfaces'
import formatDateInLocalTimezone from '@insided/shared/utils/dateFormatting'
import { FilterSelector } from '@insided/deprecated-control/overview/filters/FiltersMenu'

export interface DateFilterFilterSelectorItem {
  id: string | number
  type: 'absolute' | 'relative' | 'range'
  name: string
  value: string
  unit?: string
}

interface CommonProps {
  items: DateFilterFilterSelectorItem[]
}

interface IsoDateRange {
  from: string | undefined
  to: string | undefined
}

interface State {
  condition?: string
  data: string | number | IsoDateRange
  type?: string
  unit?: string
}

type BuilderData = BaseFilterSelectorBuilderData & CommonProps

type Filter = FilterSelector & CommonProps

type Props = FilterSelectorProps<State> & CommonProps

const getPillDisplayValue = (
  { condition, type, data, unit }: State,
  items: DateFilterFilterSelectorItem[]
): string => {
  const formatDate = (date: string) =>
    formatDateInLocalTimezone((new Date(date).getTime() / 1000) | 0)
  const item = items.find((i) => i.value === condition)
  let displayCondition = item?.name
  let displayValue
  switch (type) {
  case 'absolute':
    displayValue = formatDate(data as string)
    break
  case 'relative':
    displayValue = `${data as number} ${unit}${data > 1 ? 's' : ''} ago`
    break
  case 'range':
    displayCondition = ''
    displayValue = `${formatDate((data as IsoDateRange).from as string)} - ${formatDate(
      (data as IsoDateRange).to as string
    )}`
  }

  return `${displayCondition} ${displayValue}`
}

class DateFilterSelector extends Component<Props, State> {
  static create = (config: BuilderData): Filter => ({
    component: this,
    type: 'filter',
    getPillDisplayValue: (state: State): string => getPillDisplayValue(state, config.items),
    ...config
  })

  constructor(props: Props) {
    super(props)
    this.state = {
      condition: props.selectedFilterItems?.condition || '',
      data: props.selectedFilterItems?.data || '',
      type: props.selectedFilterItems?.type || '',
      unit: props.selectedFilterItems?.unit || ''
    }
  }

  setCondition = (condition: string, itemType: string) => {
    const { data, type } = this.state
    const alreadyAppliedFilter = this.props.selectedFilterItems

    const newData =
      type === itemType
        ? data
        : alreadyAppliedFilter?.type === itemType
          ? alreadyAppliedFilter?.data
          : ''

    this.setState({ condition, data: newData, type: itemType })
  }

  setData = (data: string | number | IsoDateRange, item: any) => {
    const { condition } = this.state
    this.setState({ condition, data, unit: item.type === 'relative' && item.unit })
  }

  getSubInputTitle = (itemUnit: string) => {
    return `${itemUnit}s ago`
  }

  getIsoDateRange(dates: DateRange): IsoDateRange {
    return {
      from: dates.from?.toISOString(),
      to: dates.to?.toISOString()
    }
  }

  render() {
    const { name, items, children } = this.props
    const { condition, data, type } = this.state

    return (
      <div>
        <div className='overview__filters-box-content'>
          {items.map((item) => {
            const checked = condition === item.value && type === item.type
            const showCalendar = item.type === 'absolute' || item.type === 'range'
            return (
              <div key={item.id}>
                <InputGroup
                  inline
                  key={item.id}
                  name={name}
                  value={item.value}
                  title={item.name}
                  type='radio'
                  formGroupClasses='overview__filters-box-filter'
                  checked={checked}
                  onChange={(e) => this.setCondition(e.target.value, item.type)}
                />
                {checked &&
                  (showCalendar ? (
                    <DatePicker
                      date={typeof data === 'string' ? new Date(data) : undefined}
                      startDate={
                        typeof data === 'object' && data.from ? new Date(data.from) : undefined
                      }
                      endDate={typeof data === 'object' && data.to ? new Date(data.to) : undefined}
                      flex
                      dateRange={item.type === 'range'}
                      onDateChange={(date: Date) => this.setData(date.toISOString(), item)}
                      onDateRangeChange={(dates: DateRange) =>
                        this.setData(this.getIsoDateRange(dates), item)}
                    />
                  ) : (
                    <InputGroup
                      inline
                      name={name}
                      value={
                        typeof data === 'number' || typeof data === 'string' ? data : undefined
                      }
                      title={this.getSubInputTitle(item.unit as string)}
                      type='number'
                      min={0}
                      formGroupClasses='overview__filters-data-input'
                      onKeyUp={(e) => this.setData(e.target.value, item)}
                      onChange={(e) => this.setData(e.target.value, item)}
                    />
                  ))}
              </div>
            )
          })}
        </div>
        {extractRenderPropsChildren(children)({
          data: { ...this.state },
          isApplyButtonDisabled:
            !condition ||
            !data ||
            (condition === 'range' && typeof data === 'object' && data.to === undefined)
        })}
      </div>
    )
  }
}

export default DateFilterSelector
